import {
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQueryClient,
} from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { getAccountListForLinkingProcedures } from '../utils/urls';

import { linkImportAccountList } from 'types/procedureBuilder';
import { ErrorModal } from 'types/modalTypes';

const pageLength = 25;

async function fetchProcedureLinks({
  accountId,
  start,
  pageLength,
  keywordSearch,
}: TVariables): Promise<TResult> {
  const response = await fetch(
    getAccountListForLinkingProcedures(
      accountId,
      start,
      pageLength,
      keywordSearch,
    ),
    {
      method: 'GET',
      headers: {
        Authorization: getTokenWithType(),
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    },
  );
  return response.json();
}

const debouncedFetch = pDebounce(fetchProcedureLinks, 500);

type TError = ErrorModal;

type TResult = {
  Data: Array<linkImportAccountList>;
  IsSuccess: boolean;
  StatusCode: number;
};

type TVariables = {
  accountId: number;
  pageLength: number;
  start: number;
  keywordSearch: string;
};

function useGetAccountListForLinkingProcedures(
  accountId: number,
  keywordSearch: string,
): UseInfiniteQueryResult<TResult, TError> {
  const queryClient = useQueryClient();
  const data: any | undefined = queryClient.getQueryData([
    'procedureLinks_list',
    accountId,
    keywordSearch,
  ]);

  const start = data && data.pages ? data.pages.length * pageLength : 0;

  return useInfiniteQuery(
    ['procedureLinks_list', accountId, keywordSearch],
    () =>
      debouncedFetch({
        accountId,
        start,
        pageLength,
        keywordSearch,
      }),
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data.length === pageLength)
          return allPages.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage?.Data.length === pageLength)
          return allPages.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetAccountListForLinkingProcedures };
